import jexl from 'jexl';
import { GoabSpacer, GoabDivider, GoabBadge, GoabIcon, GoabTable } from "@abgov/react-components";
import { DynamicFields } from "../Forms/DynamicFields";
import parse from "html-react-parser";
import { evaluate } from "../../../utils/globalUtility";

export const DynamicSpacer = ({ field }) => {
    return (
        <GoabSpacer {...field.properties}></GoabSpacer>
    );
};

export const DynamicDivider = ({ field }) => {
    return (
        <GoabDivider {...field.properties}></GoabDivider>
    );
};

export const DynamicBadge = ({ field, getResourceValue, context }) => {
  if (field.properties?.content) {
    let content = getResourceValue({
      key: "RESOURCES@" + field.properties.content,
    });

    // Replace placeholders in the value with provided params
    Object.keys(field.properties?.params || []).forEach((paramKey) => {
      const paramValue = field.properties.params[paramKey];
      const regex = new RegExp(`{${paramKey}}`, "g");
      let value = getResourceValue({ key: "RESOURCES@" + paramValue });
      try {
        const exprValue = jexl.evalSync(paramValue, context);
        if (exprValue) {
          value = exprValue;
        }
      } catch (ex) {}
      content = content.replace(regex, value);
    });
    field.properties = { ...field.properties, content: content };
  }
  return <GoabBadge {...field.properties}></GoabBadge>;
};

export const DynamicIcon = ({ field }) => {
    return (
        <GoabIcon {...field.properties}></GoabIcon>
    );
};

export const DynamicHtml = ({
  field,
  form,
  getResourceValue,
  getRefData,
  clickHandlers,
  context,
  setFormData,
}) => {
  let content = getResourceValue({
    key: "RESOURCES@" + field.properties?.content,
  });

  // Replace placeholders in the value with provided params
  Object.keys(field.properties?.params || []).forEach((paramKey) => {
    const paramValue = field.properties.params[paramKey];
    const regex = new RegExp(`{${paramKey}}`, "g");
    let value = getResourceValue({ key: "RESOURCES@" + paramValue });
    try {
      const exprValue = jexl.evalSync(paramValue, { ...context, form: form });
      if (exprValue) {
        value = exprValue;
      }
    } catch (ex) {}
    content = content.replace(regex, value);
  });

  const parsedHtml = parse(content, {
    replace: (domNode) => {
      if (domNode.name === "fields") {
        return (
          <DynamicFields
            children={field.fields}
            form={form}
            getResourceValue={getResourceValue}
            getRefData={getRefData}
            clickHandlers={clickHandlers}
            context={context}
            setFormData={setFormData}
          ></DynamicFields>
        );
      }
      // Process html elements with href attributes to use click handlers if special hrefs are found.
      else if (domNode.attribs?.href) {
        const href = domNode.attribs.href;
        const specialHref = href.substring(0, 1) === "#" && href.length > 1;

        const target = domNode.attribs.target;
        if (target !== "_blank") {
          delete domNode.attribs.target;
        } else {
          // Ensure rel="noopener" for security when target="_blank"
          domNode.attribs.rel = "noopener noreferrer";
        }

        if (specialHref) {
          const functionName = href.substring(1);
          const targetElement = document.getElementById(functionName);
          const newAttribs = { ...domNode.attribs };
          newAttribs.style = {
            cursor: "pointer",
            textDecoration: "underline",
          };
          newAttribs.onClick = (e) => {
            e.preventDefault();
            if (clickHandlers && clickHandlers[functionName]) {
              clickHandlers[functionName]();
            } else if (targetElement) {
              targetElement.focus();
              targetElement.scrollIntoView({ behavior: "smooth" });
            }
          };

          domNode.attribs = newAttribs;
        }
      }
    },
  });

  return <div>{parsedHtml}</div>;
};

export const DynamicTable = ({ field, form, getResourceValue, context }) => {
  if (field.properties?.heading) {
    field.properties = {
      ...field.properties,
      heading: getResourceValue({
        key: "RESOURCES@" + field.properties?.heading,
      }),
    };
  }

  const data = evaluate(field.data, { ...context, form: form });
  if (!field.data?.columns?.length || !data?.length) {
    return (
      <>{getResourceValue({ key: "RESOURCES@" + field.data?.noDataLabel })}</>
    );
  }

  return (
    <GoabTable {...field.properties}>
      {field.properties?.showHeader && (
        <thead>
          <tr>
            {field.data.columns.map((col, index) => {
              return (
                <th key={`column-${index}`}>
                  {getResourceValue({ key: "RESOURCES@" + col.label })}
                </th>
              );
            })}
          </tr>
        </thead>
      )}
      <tbody>
        {data.map((row, index) => {
          return (
            <tr key={row.id ?? `row-${index}`}>
              {field.data.columns.map((col, index) => {
                let content = row[col.content] ?? col.content;
                Object.keys(col.params || []).forEach((paramKey) => {
                  const regex = new RegExp(`{${paramKey}}`, "g");
                  const value = row[paramKey];
                  content = content.replace(regex, value);
                });

                return <td key={`column-${index}`}>{parse(content)}</td>;
              })}
            </tr>
          );
        })}
        </tbody>
      </GoabTable>
    );
  };